import { useEffect, useState, useCallback, useRef } from "react";

interface Props {
  duration: number;
  isStartOnMount?: boolean;
}

export const useCountDownTimer = ({ duration, isStartOnMount }: Props) => {
  const [seconds, setSeconds] = useState<number | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const getTimer = () => {
    if (seconds !== null) {
      const minutes = Math.floor((seconds % 3600) / 60);
      const remainingSeconds = seconds % 60;
      const minutesString = minutes.toString().padStart(2, "0");
      const secondsString = remainingSeconds.toString().padStart(2, "0");
      return `${minutesString}:${secondsString}`;
    }
  };

  const countDownCounter = useCallback(() => {
    let secondsLeft = duration;
    setSeconds(secondsLeft);

    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    timerRef.current = setInterval(() => {
      secondsLeft -= 1;
      setSeconds(secondsLeft);
      if (secondsLeft <= 0 && timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }, 1000);
  }, [duration]);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setSeconds(null);
  }, []);

  const restartTimer = useCallback(() => {
    clearTimer();
    countDownCounter();
  }, [clearTimer, countDownCounter]);

  useEffect(() => {
    if (isStartOnMount) {
      countDownCounter();
    }

    return () => {
      clearTimer();
    };
  }, []);

  return {
    timer: getTimer(),
    isExpired: seconds !== null && seconds <= 0,
    restartTimer,
    clearTimer,
  };
};
